import {
  Asset,
  AssetFile,
  ChainModifiers,
  DeletedAsset,
  DeletedEntry,
  Entry,
  EntryFieldTypes,
  EntrySkeletonType,
  FieldsType,
  SyncCollection,
} from 'contentful';
import { Document } from '@contentful/rich-text-types';
import { JsonObject } from '@angular-devkit/core';

export enum ContentfulContext {
  STAGING = 'staging',
  PRODUCTION = 'production',
}

export enum ContentfulSpaceTagId {
  COMMERCIAL = 'commercial',
  RECREATIONAL = 'recreational',
}

export interface ContentfulApi {
  space: string;
  host: string;
  accessToken: string;
}

export enum ContentfulSyncStatus {
  Initial = 'Initial',
  UpdateChecked = 'UpdateChecked',
  EntriesSynced = 'EntriesSynced',
  AssetsDownloaded = 'AssetsDownloaded',
  AssetsSynced = 'AssetsSynced',
  Failure = 'Failure',
}

export interface IContentfulState {
  error: string;
  nextSyncToken: string;
  viewStatus: ContentfulSyncStatus;
  entriesToCache: number;
  entriesCached: number;
  assetsToCache: number;
  assetsDownloaded: number;
  assetsCached: number;
}

export type IContentfulEntry<T extends FieldsType> = Entry<
  EntrySkeletonType<T>,
  'WITHOUT_UNRESOLVABLE_LINKS'
>;

export type ISyncCollection = SyncCollection<
  EntrySkeletonType<FieldsType, string>,
  ChainModifiers,
  string
> &
  Stringifiable;

export interface ICacheAsset {
  blob: Blob;
  type: string;
  id: string;
  url: string;
}

export const getAssetFile = (asset: Asset) => {
  const file = asset?.fields?.file;
  return file && Object.prototype.hasOwnProperty.call(file, 'en-US')
    ? (file as { 'en-US': AssetFile })['en-US']
    : (file as AssetFile);
};

export const getId = (
  content: IContentfulEntry<FieldsType> | Asset | DeletedEntry | DeletedAsset,
) => content?.sys.id;

export const contentfulNewContentMessage = `There have been updates since you last used the app. Do you want to download these updates now?<br><br><b>Warning:</b> Only tap ‘Yes’ if you have good internet connection. If not, tap ‘Cancel’ – you can keep using the app now and download the updates later.`;

export interface Stringifiable {
  stringifySafe: () => string;
}

export type ExtractFields<T extends FieldsType> = IContentfulEntry<T>['fields'];

type IContentfulEntryLinkField<T extends FieldsType> =
  EntryFieldTypes.EntryLink<EntrySkeletonType<T>>;
type IContentfulEntryArrayField<T extends FieldsType> = EntryFieldTypes.Array<
  IContentfulEntryLinkField<T>
>;

export interface IReferenceFields {
  sections: IContentfulEntryArrayField<ISectionFields>;
  placeholder: EntryFieldTypes.Text;
  title: EntryFieldTypes.Text;
}

export type IReference = ExtractFields<IReferenceFields>;

export interface ISectionFields {
  title: EntryFieldTypes.Text;
  icon: EntryFieldTypes.AssetLink;
  links: IContentfulEntryArrayField<ISectionLinkTypesFields>;
}

export type ISectionLinkTypesFields =
  | IExternalLinkFields
  | IContentFields
  | ISectionFields
  | IMapContentFields;
export type ISectionEntry = IContentfulEntry<ISectionFields>;
export type ISection = ExtractFields<ISectionFields>;

export type ISideMenu = ExtractFields<ISideMenuFields>;

export interface ISideMenuItemFields {
  title: EntryFieldTypes.Text;
  externalLink: EntryFieldTypes.Text;
  icon: EntryFieldTypes.AssetLink;
  internalLink: EntryFieldTypes.Text;
  internalReference: IContentfulEntryLinkField<IReferenceFields>;
}

export interface ISideMenuFields {
  sideMenuItems: IContentfulEntryArrayField<ISideMenuItemFields>;
  socialMediaItems: IContentfulEntryArrayField<ISocialMediaItem>;
}

export type IHome = ExtractFields<IHomeFields>;

export interface ISpeciesFields {
  caabCode: EntryFieldTypes.Integer;
  familyName: EntryFieldTypes.Text;
  commonName: EntryFieldTypes.Text;
  speciesPhotos: EntryFieldTypes.Array<EntryFieldTypes.AssetLink>;
  otherNames: EntryFieldTypes.RichText;
  scientificName: EntryFieldTypes.RichText;
  sizeRange: EntryFieldTypes.Text;
  sizeLimitsOnTakes: EntryFieldTypes.RichText;
  possessionLimitsOnTakes: EntryFieldTypes.RichText;
  possessionLimitsOnTakesTooltip: EntryFieldTypes.RichText;
  closures: EntryFieldTypes.RichText;
  frequentMisIdentification: EntryFieldTypes.RichText;
  description: EntryFieldTypes.RichText;
  distribution: EntryFieldTypes.RichText;
  freshwater: EntryFieldTypes.Boolean;
  tidal: EntryFieldTypes.Boolean;
  temporaryRestriction: EntryFieldTypes.Boolean;
  isPest: EntryFieldTypes.Boolean;
  ageLengthData?: EntryFieldTypes.AssetLink;
  stocks: EntryFieldTypes.RichText;
  detailsLink?: EntryFieldTypes.Text;
  detailsLinkTitle?: EntryFieldTypes.Text;
  detailsLinkText?: EntryFieldTypes.Text;
  speciesKeptLabel?: EntryFieldTypes.Text;
  speciesReleasedLabel?: EntryFieldTypes.Text;
}

export interface IHomeFields {
  appTitle: EntryFieldTypes.Text;
  appSubTitle: EntryFieldTypes.Text;
  appSections: IContentfulEntryArrayField<IAppSectionFields>;
  heroImage: EntryFieldTypes.AssetLink;
  localSpeciesLabel: EntryFieldTypes.Text;
  searchHintText: EntryFieldTypes.Text;
  speciesList: IContentfulEntryArrayField<ISpeciesFields>;
  whatsNewLabel?: EntryFieldTypes.Text;
  whatsNewList?: IContentfulEntryArrayField<IContentFields>;
}

export interface IAppSectionFields {
  label: EntryFieldTypes.Text;
  icon: EntryFieldTypes.AssetLink;
  internalLink: EntryFieldTypes.Text;
}

export type IAppSection = ExtractFields<IAppSectionFields>;
export type IMapContentEntry = IContentfulEntry<IMapContentFields>;
export type IMapContent = ExtractFields<IMapContentFields>;
export type IContentEntry = IContentfulEntry<IContentFields>;
export type IContent = ExtractFields<IContentFields>;

export type IWatermark = ExtractFields<IWatermarkFields>;

export interface IWatermarkItem extends Omit<
  IWatermark,
  'name' | 'speciesDetailsToggleField' | 'image'
> {
  image: string;
}

export interface ISpeciesSummaryFields {
  speciesGroups: IContentfulEntryArrayField<ISpeciesGroupFields>;
  noTakePhoto: EntryFieldTypes.AssetLink;
  noTakePhotoSubtext: EntryFieldTypes.Text;
  watermarks: IContentfulEntryArrayField<IWatermarkFields>;
}

export interface ISpeciesGroupFields {
  speciesGroup: EntryFieldTypes.Text;
  speciesList: IContentfulEntryArrayField<ISpeciesFields>;
}

export type ISpeciesEntry = IContentfulEntry<ISpeciesFields>;
export type ISpecies = ExtractFields<ISpeciesFields>;

export interface IContactFisheriesFields {
  contactFisheriesItems: IContentfulEntryArrayField<IContactFisheriesItemFields>;
}

export type IContactFisheries = ExtractFields<IContactFisheriesFields>;

export type IContactFisheriesItem = ExtractFields<IContactFisheriesItemFields>;

export type IExternalLinkEntry = IContentfulEntry<IExternalLinkFields>;
export type IExternalLink = ExtractFields<IExternalLinkFields>;

interface ISpeciesDisplayName extends JsonObject {
  [key: string]: string;
}

export interface ISpeciesRulesContentFields {
  rulesContained: EntryFieldTypes.Array<EntryFieldTypes.Symbol>;
  displayName: EntryFieldTypes.Object<ISpeciesDisplayName>;
}

export interface ISpeciesDetailsContentFields {
  detailsContained: EntryFieldTypes.Array<EntryFieldTypes.Symbol>;
  displayName: EntryFieldTypes.Object<ISpeciesDisplayName>;
}

export interface IAttributes extends JsonObject {
  [key: string]: Array<IAttribute>;
}

export interface IAttribute extends JsonObject {
  attributeName: string;
  displayName: string;
}

export interface IMapContentFields {
  title: EntryFieldTypes.Text;
  featureLayers: EntryFieldTypes.Array<EntryFieldTypes.AssetLink>;
  queryFeatureLayers: EntryFieldTypes.Array<EntryFieldTypes.AssetLink>;
  icon: EntryFieldTypes.AssetLink;
  baseLayers: EntryFieldTypes.Array<EntryFieldTypes.AssetLink>;
  attributes: EntryFieldTypes.Object<IAttributes>;
}

export interface IContentFields {
  title: EntryFieldTypes.Text;
  content: EntryFieldTypes.RichText;
  icon: EntryFieldTypes.AssetLink;
  internalUrl?: EntryFieldTypes.Text;
}

export interface ISocialMediaItem {
  socialMediaType: EntryFieldTypes.Text<'Facebook' | 'Youtube' | 'Instagram'>;
  socialMediaLink: EntryFieldTypes.Text;
}

export interface IAppInfoFields {
  appVersion: EntryFieldTypes.Text;
  versionAvailableText: EntryFieldTypes.Text;
  boatrampLayer: EntryFieldTypes.AssetLink;
  sipsLayer: EntryFieldTypes.AssetLink;
}

export interface IWatermarkFields {
  name: EntryFieldTypes.Text;
  speciesDetailsToggleField: EntryFieldTypes.Text;
  image: EntryFieldTypes.AssetLink;
  subtext?: EntryFieldTypes.Text;
  ruleOrDetail?: EntryFieldTypes.Text;
  scrollToCardOnClick: EntryFieldTypes.Boolean;
  showIconOnCard: EntryFieldTypes.Boolean;
}

export interface IContactFisheriesItemFields {
  label: EntryFieldTypes.Text;
  displayText: EntryFieldTypes.RichText;
  url: EntryFieldTypes.Text;
  buttonLabel: EntryFieldTypes.Text;
  secondDisplayText: EntryFieldTypes.RichText;
  secondUrl: EntryFieldTypes.Text;
  secondButtonLabel: EntryFieldTypes.Text;
}

export interface IExternalLinkFields {
  title: EntryFieldTypes.Text;
  externalLink: EntryFieldTypes.Text;
  icon: EntryFieldTypes.AssetLink;
}

export type ISideMenuItem = ExtractFields<ISideMenuItemFields>;

export interface ITepSpeciesDetailItem {
  fieldId: string;
  label: string;
  value: number | string | Document;
  tooltip?: string;
}

interface ITepSpeciesDisplayName extends JsonObject {
  [key: string]: string;
}

export interface ITepSpeciesKeyValueFields {
  detailsContained: EntryFieldTypes.Array<EntryFieldTypes.Symbol>;
  displayName: EntryFieldTypes.Object<ITepSpeciesDisplayName>;
}

export type ISpeciesIdInfo = ExtractFields<ISpeciesIdFields>;

export interface ITepSpeciesFields {
  caabCode: EntryFieldTypes.Integer;
  commonName: EntryFieldTypes.Text;
  familyName: EntryFieldTypes.Text;
  speciesPhotos: EntryFieldTypes.Array<EntryFieldTypes.AssetLink>;
  otherNames: EntryFieldTypes.RichText;
  scientificName: EntryFieldTypes.RichText;
  description: EntryFieldTypes.RichText;
  distribution: EntryFieldTypes.RichText;
  frequentMisIdentification: EntryFieldTypes.RichText;
  sizeRange: EntryFieldTypes.RichText;
  dos: EntryFieldTypes.RichText;
  donts: EntryFieldTypes.RichText;
}

export interface ITepSpeciesGroupFields {
  speciesGroup: EntryFieldTypes.Text;
  speciesList: IContentfulEntryArrayField<ITepSpeciesFields>;
  image: EntryFieldTypes.AssetLink;
}

export interface ITepSpeciesSummaryFields {
  speciesGroups: IContentfulEntryArrayField<ITepSpeciesGroupFields>;
}

export type ITepSpeciesEntry = IContentfulEntry<ITepSpeciesFields>;
export type ITepSpecies = ExtractFields<ITepSpeciesFields>;

export interface IWatermarkFields {
  name: EntryFieldTypes.Text;
  speciesDetailsToggleField: EntryFieldTypes.Text;
  image: EntryFieldTypes.AssetLink;
  subtext?: EntryFieldTypes.Text;
  ruleOrDetail?: EntryFieldTypes.Text;
  scrollToCardOnClick: EntryFieldTypes.Boolean;
  showIconOnCard: EntryFieldTypes.Boolean;
}

export interface ITepSpeciesSummaryFields {
  speciesGroups: IContentfulEntryArrayField<ITepSpeciesGroupFields>;
  noTakePhoto: EntryFieldTypes.AssetLink;
  noTakePhotoSubtext: EntryFieldTypes.Text;
  watermarks: IContentfulEntryArrayField<IWatermarkFields>;
}

export interface ISpeciesIdFields {
  speciesIdItems: IContentfulEntryArrayField<IExternalLinkFields>;
}
